import type {
  DutyHandoffConfig,
  TransferOutcome,
  VapiMessage,
} from '@/features/calls/types'
import { parseArgs } from '@/features/calls/utils/deriveToolInvocations'

/** Transfer happened and its outcome was captured (`transfer_outcome` set). */
export const ENDED_REASON_FORWARDED = 'assistant-forwarded-call'
/** Blind-transfer failure. */
export const ENDED_REASON_TRANSFER_FAILED =
  'call.in-progress.error-transfer-failed'
/**
 * Warm-transfer failures — Vapi emits a family of ended reasons
 * (e.g. `call.in-progress.error-warm-transfer-max-duration`,
 * `…-silence-timeout`), so match on the shared prefix.
 */
const ENDED_REASON_WARM_TRANSFER_FAILED_PREFIX =
  'call.in-progress.error-warm-transfer-'

/**
 * Relay's Twilio lookup sometimes finds no child call for a forwarded call
 * and stores this literal sentinel as `transfer_outcome.status` — it is not a
 * real outcome, so it must not render as a raw "No_child_call" badge.
 */
const OUTCOME_STATUS_NO_CHILD_CALL = 'no_child_call'

/**
 * Relay's `record_transfer_outcome` returns early once `transfer_outcome` is
 * set, so a status captured while the transferred leg was still live never
 * gets refreshed — a transfer that went on to complete can be stuck showing
 * one of these forever. None of them are in the Twilio dial-status map
 * either, so left alone they'd hit the raw hyphen-capitalize fallback. Same
 * treatment as the `no_child_call` sentinel: "a transfer happened, outcome
 * unknown" rather than a stale in-progress badge.
 */
const OUTCOME_STATUS_STILL_LIVE = new Set(['in-progress', 'ringing', 'queued'])

/** True when `status` means "not a settled outcome" — see the two constants above. */
function isUnsettledOutcomeStatus(status: string): boolean {
  return (
    status === OUTCOME_STATUS_NO_CHILD_CALL ||
    OUTCOME_STATUS_STILL_LIVE.has(status)
  )
}

/** True when the call ended because a transfer attempt failed. */
export function isTransferFailureReason(
  endedReason: string | null | undefined
): boolean {
  if (!endedReason) return false
  return (
    endedReason === ENDED_REASON_TRANSFER_FAILED ||
    endedReason.startsWith(ENDED_REASON_WARM_TRANSFER_FAILED_PREFIX)
  )
}

export interface TransferDisplayInfo {
  /**
   * completed  → `transfer_outcome` captured; `connected`/`status`/`duration`
   *   describe how the transferred leg went.
   * forwarded  → transfer happened but no outcome was captured (this also
   *   covers a captured outcome whose status is unsettled — the
   *   `no_child_call` sentinel, or a live in-progress/ringing/queued status
   *   relay never came back to refresh).
   * attempted  → a `transferCall` request went out but the call ended some
   *   other way (e.g. the caller hung up mid-dial) — no outcome and no
   *   transfer-related ended_reason, so nothing else can be claimed.
   * failed     → transfer was attempted and failed.
   */
  kind: 'completed' | 'forwarded' | 'attempted' | 'failed'
  destinationNumber: string | null
  /** Destination name resolved from the duty's handoff_config, if any. */
  destinationName: string | null
  /** Raw `transfer_outcome.status` (completed-kind only). */
  status: string | null
  /** True when the transferred leg was answered by a person (not voicemail). */
  connected: boolean
  /**
   * True when Twilio's answering-machine detection reports the transferred
   * leg reached voicemail rather than a person (completed-kind only, and
   * only ever true when `connected` is false).
   */
  reachedVoicemail: boolean
  /** Seconds the transferred leg lasted (completed-kind only). */
  duration: number | null
}

/** True when `messages` contains at least one `transferCall` tool request. */
function hasTransferCallRequest(
  messages: VapiMessage[] | null | undefined
): boolean {
  for (const message of Array.isArray(messages) ? messages : []) {
    if (
      typeof message !== 'object' ||
      message === null ||
      message.role !== 'tool_calls'
    ) {
      continue
    }
    const toolCalls = Array.isArray(message.toolCalls) ? message.toolCalls : []
    if (
      toolCalls.some(
        (tc) =>
          typeof tc === 'object' &&
          tc !== null &&
          tc.function?.name === 'transferCall'
      )
    ) {
      return true
    }
  }
  return false
}

/**
 * Intended destination from the last `transferCall` request's arguments JSON.
 *
 * The call's `messages` array carries VAPI messages with `role: 'tool_calls'`
 * — the actual tool call requests nest one level down, under each message's
 * `toolCalls[]` (the exact shape `deriveToolInvocations` already reads from
 * `messages` for the Tools tab).
 *
 * A call can retry a transfer to a different destination, so the LAST
 * transferCall request is the one the outcome/ended_reason actually pertains
 * to — scan in reverse rather than returning on the first match.
 *
 * `messages` is unconstrained jsonb, so every level is defensively guarded: a
 * malformed message or tool call is skipped rather than thrown.
 */
function findRequestedDestination(
  messages: VapiMessage[] | null | undefined
): string | null {
  const msgs = Array.isArray(messages) ? messages : []
  for (const message of msgs.toReversed()) {
    if (
      typeof message !== 'object' ||
      message === null ||
      message.role !== 'tool_calls'
    ) {
      continue
    }
    const toolCalls = Array.isArray(message.toolCalls) ? message.toolCalls : []
    for (const toolCall of toolCalls.toReversed()) {
      if (
        typeof toolCall !== 'object' ||
        toolCall === null ||
        toolCall.function?.name !== 'transferCall'
      ) {
        continue
      }
      const destination = parseArgs(toolCall).destination
      if (typeof destination === 'string' && destination.trim()) {
        return destination.trim()
      }
    }
  }
  return null
}

/**
 * Loose phone-number equality: compare trailing digits so "+1 (555) 123-4567"
 * matches "5551234567". Requires at least 7 shared digits to avoid matching
 * extensions or empty strings.
 */
function sameNumber(a: string, b: string): boolean {
  const digitsA = a.replace(/\D/g, '')
  const digitsB = b.replace(/\D/g, '')
  if (!digitsA || !digitsB) return false
  const [shorter, longer] =
    digitsA.length <= digitsB.length ? [digitsA, digitsB] : [digitsB, digitsA]
  return shorter.length >= 7 && longer.endsWith(shorter)
}

/**
 * Destination name (`description`) from the duty's handoff_config, if any.
 *
 * Real duty configs can have several destinations sharing one number,
 * distinguished only by `extension` — which `transfer_outcome.to` never
 * carries. So this collects every number-matching destination and only
 * returns a name when they agree on exactly one: a wrong confident name is
 * worse than falling back to the plain number.
 */
function resolveDestinationName(
  destinationNumber: string | null,
  handoffConfig: DutyHandoffConfig | null | undefined
): string | null {
  if (!destinationNumber) return null
  const matchingNames = new Set<string>()
  for (const destination of handoffConfig?.destinations ?? []) {
    if (
      typeof destination?.number === 'string' &&
      sameNumber(destination.number, destinationNumber)
    ) {
      const name = destination.description?.trim()
      if (name) matchingNames.add(name)
    }
  }
  return matchingNames.size === 1 ? [...matchingNames][0]! : null
}

/**
 * Builds the shared shape for every "a transfer happened/was attempted, but
 * nothing about its outcome can be claimed" result (`forwarded`, `attempted`,
 * `failed`, and the unsettled-outcome-status case folded into `forwarded`).
 */
function buildUnclaimedResult(
  kind: 'forwarded' | 'attempted' | 'failed',
  destinationNumber: string | null,
  handoffConfig: DutyHandoffConfig | null | undefined
): TransferDisplayInfo {
  return {
    kind,
    destinationNumber,
    destinationName: resolveDestinationName(destinationNumber, handoffConfig),
    status: null,
    connected: false,
    reachedVoicemail: false,
    duration: null,
  }
}

/**
 * Decide whether (and what) the call sheet's Transfer section shows.
 * Returns null when the call carries no transfer information at all.
 */
export function deriveTransferInfo(detail: {
  transfer_outcome?: TransferOutcome | null
  ended_reason?: string | null
  messages?: VapiMessage[] | null
  duty_handoff_config?: DutyHandoffConfig | null
}): TransferDisplayInfo | null {
  const requestedDestination = findRequestedDestination(detail.messages)
  const outcome = detail.transfer_outcome

  if (outcome) {
    const destinationNumber =
      (typeof outcome.to === 'string' && outcome.to.trim()) ||
      requestedDestination
    const status = typeof outcome.status === 'string' ? outcome.status : null

    // Not a settled outcome — either Twilio found no child call for the
    // forward, or the status was captured while the transferred leg was
    // still live and relay never came back to refresh it. Either way, treat
    // it like an uncaptured outcome rather than a raw/stale status badge.
    if (status !== null && isUnsettledOutcomeStatus(status)) {
      return buildUnclaimedResult(
        'forwarded',
        destinationNumber || null,
        detail.duty_handoff_config
      )
    }

    const duration =
      typeof outcome.duration === 'number' ? outcome.duration : null
    // AMD only runs when enabled on the call, so `answered_by` is commonly
    // absent — that (not just literally `'human'`) still counts as
    // "connected", preserving the pre-AMD default. Any other value —
    // `machine_start`/`machine_end_*` (voicemail), `fax`, `unknown` — means
    // no human answered, so `connected` requires an exact match rather than
    // merely "doesn't start with machine". (If AMD is not actually enabled
    // on these transfers yet, this whole check is a no-op; it activates
    // automatically the day it is.)
    const answeredBy =
      typeof outcome.answered_by === 'string' ? outcome.answered_by : null
    const reachedVoicemail = answeredBy?.startsWith('machine') ?? false
    const amdConnected = answeredBy === null || answeredBy === 'human'
    return {
      kind: 'completed',
      destinationNumber: destinationNumber || null,
      destinationName: resolveDestinationName(
        destinationNumber || null,
        detail.duty_handoff_config
      ),
      status,
      connected: status === 'completed' && (duration ?? 0) > 0 && amdConnected,
      reachedVoicemail,
      duration,
    }
  }

  const kind =
    detail.ended_reason === ENDED_REASON_FORWARDED
      ? ('forwarded' as const)
      : isTransferFailureReason(detail.ended_reason)
        ? ('failed' as const)
        : hasTransferCallRequest(detail.messages)
          ? ('attempted' as const)
          : null
  if (!kind) return null

  return buildUnclaimedResult(
    kind,
    requestedDestination,
    detail.duty_handoff_config
  )
}
